import React, { useState, useEffect } from 'react';
import { RefreshCw, XCircle, TrendingUp, TrendingDown, ExternalLink, Zap } from 'lucide-react';
import { useWalletUser } from '../../hooks/useWalletUser';
import { supabase } from '../../services/supabaseService';

// Interface for strategy
interface Strategy {
  id: string;
  name: string;
  user_wallet_address: string;
  trading_wallet_address: string;
}

// Interface for trade positions
interface TradePosition {
  id: string;
  strategy_id: string;
  strategy_name?: string;
  strategy?: Strategy;
  rule_id: string;
  rule_name?: string;
  token_address: string;
  token_symbol?: string;
  token_image_url?: string;
  position_type: 'long' | 'short';
  amount: string;
  price?: string;
  current_price?: string;
  transaction_signature?: string;
  close_transaction_signature?: string;
  status: 'pending' | 'completed' | 'failed' | 'closed';
  executed_at: string;
  closed_at?: string;
  profit_loss?: number;
  profit_loss_percentage?: number;
}

const AutoTraderPositions: React.FC = () => {
  const walletUser = useWalletUser();
  const [positions, setPositions] = useState<TradePosition[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isClosingPosition, setIsClosingPosition] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch positions on component mount
  useEffect(() => {
    if (walletUser.walletAddress) {
      fetchPositions();
    }
  }, [walletUser.walletAddress]);

  // Fetch positions from database
  const fetchPositions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Query positions from the auto_trader_executions table
      const { data, error } = await supabase
        .from('auto_trader_executions')
        .select(`
          *,
          strategy:auto_trader_strategies(id, name, user_wallet_address, trading_wallet_address),
          rule:auto_trader_rules(id, name)
        `)
        .eq('status', 'completed') // Only show completed positions that are not closed
        .not('status', 'eq', 'closed')
        .order('executed_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Map the data to our interface and filter by current user
      const userPositions = data
        .filter(pos => pos.strategy?.user_wallet_address === walletUser.walletAddress)
        .map(pos => ({
          id: pos.id,
          strategy_id: pos.strategy_id,
          strategy_name: pos.strategy?.name,
          strategy: pos.strategy,
          rule_id: pos.rule_id,
          rule_name: pos.rule?.name,
          token_address: pos.token_address,
          token_symbol: pos.token_symbol,
          position_type: pos.position_type,
          amount: pos.amount,
          price: pos.price,
          transaction_signature: pos.transaction_signature,
          status: pos.status,
          executed_at: pos.executed_at,
          closed_at: pos.closed_at,
          profit_loss: pos.profit_loss,
          profit_loss_percentage: pos.profit_loss_percentage
        }));

      // Fetch token details for each position
      const positionsWithTokenInfo = await Promise.all(
        userPositions.map(async (position) => {
          try {
            // Try to get token information from either tokens, new_tokens, graduating_tokens, or graduated_tokens tables
            let tokenInfo = null;
            const tables = ['tokens', 'new_tokens', 'graduating_tokens', 'graduated_tokens'];
            
            for (const table of tables) {
              const { data } = await supabase
                .from(table)
                .select('symbol, name, image_uri')
                .eq('mint', position.token_address)
                .maybeSingle();
              
              if (data) {
                tokenInfo = data;
                break;
              }
            }
            
            if (tokenInfo) {
              return {
                ...position,
                token_symbol: position.token_symbol || tokenInfo.symbol,
                token_image_url: tokenInfo.image_uri
              };
            }
            
            return position;
          } catch (err) {
            console.error(`Error fetching token info for ${position.token_address}`, err);
            return position;
          }
        })
      );

      setPositions(positionsWithTokenInfo);
    } catch (err) {
      console.error('Error fetching positions:', err);
      setError('Failed to load your trading positions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Close a position
  const closePosition = async (position: TradePosition) => {
    try {
      if (!position.strategy?.trading_wallet_address) {
        setError('Missing trading wallet information.');
        return;
      }

      setIsClosingPosition(position.id);
      setError(null);
      setSuccessMessage(null);

      // Call the auto-trader edge function to close the position
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/auto-trader/close-position`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          strategyId: position.strategy_id,
          positionId: position.id,
          userWalletAddress: walletUser.walletAddress,
          tradingWalletAddress: position.strategy.trading_wallet_address
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to close position');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccessMessage(`Position in ${position.token_symbol || 'token'} closed successfully.`);
        // Refresh positions after closing
        fetchPositions();
      } else {
        setError(result.message || 'Failed to close position');
      }
    } catch (err) {
      console.error('Error closing position:', err);
      setError(err instanceof Error ? err.message : 'Failed to close position. Please try again later.');
    } finally {
      setIsClosingPosition(null);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format transaction signature for display
  const formatTxSignature = (signature?: string) => {
    if (!signature) return 'N/A';
    return `${signature.slice(0, 6)}...${signature.slice(-4)}`;
  };

  // Get Solana explorer URL for a transaction
  const getSolanaExplorerUrl = (signature?: string) => {
    if (!signature) return '#';
    return `https://solscan.io/tx/${signature}`;
  };

  // Format profit/loss
  const formatProfitLoss = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${value >= 0 ? '+' : ''}${value.toFixed(4)} SOL`;
  };

  // Format profit/loss percentage
  const formatProfitLossPercentage = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Get CSS class for profit/loss display
  const getProfitLossClass = (value?: number) => {
    if (value === undefined) return 'text-gray-400';
    return value >= 0 ? 'text-green-400' : 'text-red-400';
  };

  return (
    <div className="w-full h-full flex flex-col bg-white overflow-hidden">
      {/* Title and refresh button */}
      <div className="flex justify-between items-center px-2 py-2 border-b border-gray-200">
        <h3 className="text-[#24c35b] text-sm font-medium">Active Positions</h3>
        <div className="flex items-center gap-2">
          <button 
            onClick={fetchPositions}
            className="text-gray-600 hover:text-black p-1 rounded transition-colors hover:bg-gray-100"
            title="Refresh positions"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="p-2 mb-2 bg-green-50 border border-green-200 rounded-md flex items-start gap-2 m-2">
          <Zap size={14} className="text-green-500 flex-shrink-0 mt-1" />
          <p className="text-xs text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="p-2 mb-2 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 m-2">
          <XCircle size={14} className="text-red-500 flex-shrink-0 mt-1" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Positions list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && positions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw size={20} className="animate-spin text-[#24c35b] mr-2" />
            <p className="text-sm text-gray-600">Loading positions...</p>
          </div>
        ) : positions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-600">No active positions found</p>
          </div>
        ) : (
          <div className="space-y-2 p-2">
            {positions.map(position => (
              <div 
                key={position.id}
                className="p-3 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      {position.token_image_url && (
                        <img 
                          src={position.token_image_url} 
                          alt={position.token_symbol || 'Token'} 
                          className="w-6 h-6 rounded-full object-cover border border-gray-200"
                        />
                      )}
                      <span className="font-medium text-black">{position.token_symbol || position.token_address.slice(0, 6) + '...'}</span>
                      <span className={`text-xs px-2 rounded-full ${position.position_type === 'long' ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'}`}>
                        {position.position_type === 'long' ? 'Long' : 'Short'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {position.strategy_name || 'Strategy'} • {formatDate(position.executed_at)}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-gray-500">Amount</p>
                        <p className="text-sm text-black font-medium">{position.amount} SOL</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">P/L</p>
                        <p className={`text-sm ${getProfitLossClass(position.profit_loss_percentage)}`}>
                          {formatProfitLossPercentage(position.profit_loss_percentage)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <a 
                      href={getSolanaExplorerUrl(position.transaction_signature)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-[#24c35b] flex items-center mb-2 transition-colors"
                    >
                      <span className="mr-1">{formatTxSignature(position.transaction_signature)}</span>
                      <ExternalLink size={10} />
                    </a>
                    <button
                      onClick={() => closePosition(position)}
                      disabled={isClosingPosition === position.id || !position.strategy?.trading_wallet_address}
                      className={`px-3 py-1 text-xs rounded 
                        ${isClosingPosition === position.id 
                          ? 'bg-gray-400 text-white' 
                          : !position.strategy?.trading_wallet_address
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-red-500 hover:bg-red-600 text-white'
                        } transition-colors font-medium`}
                    >
                      {isClosingPosition === position.id 
                        ? 'Closing...' 
                        : !position.strategy?.trading_wallet_address
                          ? 'Missing Wallet'
                          : 'Close Position'
                      }
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoTraderPositions; 